import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { ArrowLeft, EnvelopeSimple, Copy, Link, CalendarBlank, Trash, Bell } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { ClientInfoTab } from '../components/client/ClientInfoTab';
import { ClientOnboardingTab } from '../components/client/ClientOnboardingTab';
import { ClientInvoicesTab } from '../components/client/ClientInvoicesTab';
import { ClientBankStatementsTab } from '../components/client/ClientBankStatementsTab';
import { ClientAllDocsTab } from '../components/client/ClientAllDocsTab';
import { ClientFormsTab } from '../components/client/ClientFormsTab';
import { ClientMatchingTab } from '../components/client/ClientMatchingTab';

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [templates, setTemplates] = useState([]);
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [formAssignments, setFormAssignments] = useState([]);
  const [selectedFormTemplate, setSelectedFormTemplate] = useState('');
  const [reminders, setReminders] = useState([]);
  const [actionCount, setActionCount] = useState(0);

  const fetchClient = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch (error) {
      toast.error(formatApiError(error));
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchTemplates = useCallback(async () => {
    try { const { data } = await api.get('/letter-templates'); setTemplates(data); }
    catch { toast.error('Failed to load templates'); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGeneratedLetters = useCallback(async () => {
    try { const { data } = await api.get(`/generated-letters?client_id=${id}`); setGeneratedLetters(data); }
    catch { toast.error('Failed to load generated letters'); }
  }, [id]);

  const fetchFormData = useCallback(async () => {
    try {
      const [templatesRes, assignmentsRes] = await Promise.all([
        api.get('/form-templates'),
        api.get(`/clients/${id}/form-assignments`),
      ]);
      setFormTemplates(templatesRes.data);
      setFormAssignments(assignmentsRes.data);
      // Count pending action items for the client
      const pendingForms = assignmentsRes.data.filter(f => f.status === 'pending').length;
      setActionCount(pendingForms);
    } catch { toast.error('Failed to load form data'); }
  }, [id]);

  const fetchReminders = useCallback(async () => {
    try { const { data } = await api.get(`/clients/${id}/scheduled-reminders`); setReminders(data); }
    catch (error) { console.error('Failed to load reminders:', error); }
  }, [id]);

  useEffect(() => {
    fetchClient(); fetchTemplates(); fetchGeneratedLetters(); fetchFormData(); fetchReminders();
  }, [id, fetchClient, fetchTemplates, fetchGeneratedLetters, fetchFormData, fetchReminders]);

  const handleInviteClient = async () => {
    setInviting(true);
    try {
      const { data } = await api.post(`/clients/${id}/invite`);
      setInviteLink(data.invite_link);
      setInviteDialogOpen(true);
      toast.success('Invite link generated!');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setInviting(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div></div>;
  }
  if (!client) return null;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={() => navigate('/clients')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="heading-2 truncate">{client.name}</h1>
              <Badge variant="secondary" className={client.status === 'onboarded' ? 'status-onboarded' : 'status-pending'}>
                {client.status || 'pending'}
              </Badge>
              {client.portal_enabled && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Portal Active</Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm truncate">{client.email}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="self-start sm:self-center flex-shrink-0" onClick={handleInviteClient} disabled={inviting} data-testid="invite-client-btn">
          <EnvelopeSimple className="w-4 h-4 mr-2" />
          {inviting ? 'Sending...' : client.portal_enabled ? 'Resend Invite' : 'Invite to Portal'}
        </Button>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Client Portal Invite</DialogTitle>
            <DialogDescription>Share this link with {client.name} so they can set up their portal account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
              <Link className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <Input value={inviteLink} readOnly className="border-0 bg-transparent p-0 h-auto focus-visible:ring-0" data-testid="invite-link-input" />
              <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success('Link copied!'); }} data-testid="copy-link-btn">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">This link expires in 3 months. The client will use it to set their password and access the portal.</p>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="details" className="space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex h-auto gap-1 w-max md:w-auto md:flex-wrap">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="client-documents" data-testid="tab-client-documents">Client Documents</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="bank-statements" data-testid="tab-bank-statements">Bank Statements</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="forms" data-testid="tab-forms">
              Forms
              {actionCount > 0 && <Badge className="ml-1.5 bg-red-500 text-white text-[10px] py-0 px-1.5">{actionCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="matching" data-testid="tab-matching">Matching</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="details">
          <ClientInfoTab client={client} onClientUpdate={setClient} />
        </TabsContent>

        <TabsContent value="client-documents">
          <ClientOnboardingTab
            client={client}
            templates={templates}
            generatedLetters={generatedLetters}
            onRefreshClient={fetchClient}
            onRefreshLetters={fetchGeneratedLetters}
          />
        </TabsContent>

        <TabsContent value="invoices">
          <ClientInvoicesTab clientId={id} />
        </TabsContent>

        <TabsContent value="bank-statements">
          <ClientBankStatementsTab clientId={id} />
        </TabsContent>

        <TabsContent value="documents">
          <ClientAllDocsTab clientId={id} />
        </TabsContent>

        <TabsContent value="forms">
          <ClientFormsTab
            clientId={id}
            formTemplates={formTemplates}
            formAssignments={formAssignments}
            selectedFormTemplate={selectedFormTemplate}
            onSelectTemplate={setSelectedFormTemplate}
            onRefresh={fetchFormData}
          />
        </TabsContent>

        <TabsContent value="matching">
          <ClientMatchingTab clientId={id} clientName={client.name} />
        </TabsContent>
      </Tabs>

      {/* Scheduled Reminders Section */}
      {reminders.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="heading-4 flex items-center gap-2"><CalendarBlank className="w-5 h-5" /> Scheduled Reminders</CardTitle>
            <CardDescription>Upcoming auto-shares for this client</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {reminders.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-4 py-3" data-testid={`reminder-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={r.type === 'form' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}>
                      {r.type === 'form' ? 'Form' : 'Document'}
                    </Badge>
                    <div>
                      <p className="font-medium text-sm">{r.form_name || r.document_name || r.template_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Next: <strong>{r.next_run_date}</strong> | {r.frequency === 'one-off' ? 'One-off' : `Repeats ${r.frequency}`}
                        {r.run_count > 0 && ` | Sent ${r.run_count} time${r.run_count > 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={r.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'}>{r.status}</Badge>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => {
                      if (!window.confirm('Delete this reminder?')) return;
                      try { await api.delete(`/clients/${id}/scheduled-reminders/${r.id}`); fetchReminders(); toast.success('Reminder deleted'); }
                      catch (error) { toast.error(formatApiError(error)); }
                    }} data-testid={`delete-reminder-${r.id}`}>
                      <Trash className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
